/**
 * [PURPOSE]: Stone 10 (#80) — single source of truth for tab grouping.
 *   MobileNavigation already declares tabs in clusters with comments
 *   ("Core character tabs / Commerce & Items / Gameplay tools / DM
 *   Campaign tools / System"); this config makes the grouping data,
 *   so the same metadata can drive the More-menu section headers, the
 *   eventual group-visibility settings toggle, and any future system-
 *   specific tab layout (PF2e DM tools, CoC keeper screen, etc.).
 *
 * [WHY]: The audit flagged "21-tab bloated nav" — the count is RAW; the
 *   substrate question is whether the UX organizes the tabs into
 *   skimmable clusters. This config lays the data layer; UI consumers
 *   are added incrementally (Phase 1: More-menu headers; Phase 2: group
 *   visibility toggle in Settings).
 *
 * [RULE 11]: groupId is a property; consumers should never branch on
 *   group label strings. Use the constants exported here.
 *
 * [RULE 22]: Pure data + helpers; tests import from this file and
 *   the consumers (MobileNavigation, App.jsx) also import from here.
 *   No inline replicas.
 */

export const TAB_GROUPS = Object.freeze({
  CHARACTER: { id: 'character', label: 'Character', order: 1 },
  ITEMS: { id: 'items', label: 'Items & Commerce', order: 2 },
  PLAY: { id: 'play', label: 'Gameplay', order: 3 },
  PARTY: { id: 'party', label: 'Party & Combat', order: 4 },
  CAMPAIGN: { id: 'campaign', label: 'Campaign Tools', order: 5 },
  TOOLS: { id: 'tools', label: 'Workshop & System', order: 6 },
});

// Map each tab id → its group. Mirrors the existing MobileNavigation
// clusters so the More-menu can show headers without changing tab order.
export const TAB_TO_GROUP = Object.freeze({
  // CHARACTER cluster — core sheet
  character: TAB_GROUPS.CHARACTER.id,
  skills: TAB_GROUPS.CHARACTER.id,
  feats: TAB_GROUPS.CHARACTER.id,
  spellbook: TAB_GROUPS.CHARACTER.id,
  // ITEMS cluster — inventory, shop, magic, vaults
  inventory: TAB_GROUPS.ITEMS.id,
  shop: TAB_GROUPS.ITEMS.id,
  magic: TAB_GROUPS.ITEMS.id,
  vault: TAB_GROUPS.ITEMS.id,
  // PLAY cluster — dice, rest, downtimes, calendar
  dice: TAB_GROUPS.PLAY.id,
  rest: TAB_GROUPS.PLAY.id,
  downtimes: TAB_GROUPS.PLAY.id,
  calendar: TAB_GROUPS.PLAY.id,
  // PARTY cluster — party + combat manager
  party: TAB_GROUPS.PARTY.id,
  combat: TAB_GROUPS.PARTY.id,
  roster: TAB_GROUPS.PARTY.id,
  // CAMPAIGN cluster — DM authoring & worldbuilding
  bastion: TAB_GROUPS.CAMPAIGN.id,
  journal: TAB_GROUPS.CAMPAIGN.id,
  tables: TAB_GROUPS.CAMPAIGN.id,
  // TOOLS cluster — workshop + logs + settings
  workshop: TAB_GROUPS.TOOLS.id,
  log: TAB_GROUPS.TOOLS.id,
  settings: TAB_GROUPS.TOOLS.id,
});

/**
 * [4B-tab-visibility-settings — 2026-05-30]: Human-readable display labels
 * for each tab id. Single source of truth so SettingsPanel and any future
 * tab-label consumer don't hand-roll their own capitalize() hacks.
 * Labels mirror the MobileNavigation label strings exactly (e.g. 'Spells'
 * not 'Spellbook', 'Magic Items' not 'Magic').
 */
export const TAB_LABELS = Object.freeze({
  character: 'Character',
  inventory: 'Inventory',
  spellbook: 'Spells',
  skills: 'Skills',
  feats: 'Feats',
  shop: 'Shop',
  magic: 'Magic Items',
  vault: 'Vaults',
  dice: 'Dice',
  rest: 'Rest',
  downtimes: 'Downtimes',
  calendar: 'Calendar',
  bastion: 'Bastion',
  party: 'Party',
  combat: 'Combat',
  journal: 'Journal',
  tables: 'Roll Tables',
  workshop: 'Workshop',
  roster: 'Roster',
  log: 'Activity Log',
  settings: 'Settings',
});

/**
 * [4B-tab-visibility-settings — 2026-05-30]: Tabs that are always visible and
 * cannot be hidden by the user. Character is the home screen; Workshop hosts
 * all content creation; Settings is the escape hatch. Roster is separately
 * gated via settings.enableRosterTab (not included here).
 */
export const ALWAYS_VISIBLE_TABS = Object.freeze(['character', 'workshop', 'settings']);

/**
 * [4B-tab-visibility-settings — 2026-05-30]: Ordered list of toggleable tabs
 * (all tabs registered in TAB_TO_GROUP minus ALWAYS_VISIBLE_TABS), grouped
 * by TAB_GROUPS order. SettingsPanel imports this to build the checkbox grid
 * without hardcoding the tab list.
 *
 * Roster is EXCLUDED from this list because it has its own dedicated
 * `enableRosterTab` toggle in SettingsPanel. Including it here would add a
 * second checkbox that writes to hiddenTabs but has no runtime effect —
 * the render path is gated on enableRosterTab, not hiddenTabs, so the
 * hiddenTabs checkbox is misleading and harmless-but-useless.
 * [4B-allTabs-roster-fix — 2026-05-30]
 *
 * Returns: [{ group: TAB_GROUPS value, tabs: string[] }, ...]
 */

// Tabs with their own dedicated enable/disable lever that is NOT hiddenTabs.
// These are excluded from the general checkbox grid to avoid duplicate or
// misleading controls.
const SEPARATELY_GATED_TABS = Object.freeze(['roster']);

export function getToggleableTabsByGroup() {
  const alwaysSet = new Set(ALWAYS_VISIBLE_TABS);
  const separateSet = new Set(SEPARATELY_GATED_TABS);
  // Collect toggleable ids in TAB_TO_GROUP insertion order, skipping tabs
  // that are always-visible or have a separate dedicated toggle.
  const toggleable = Object.keys(TAB_TO_GROUP).filter(
    id => !alwaysSet.has(id) && !separateSet.has(id)
  );
  // Bucket by group, preserving group order
  const buckets = new Map();
  for (const id of toggleable) {
    const groupId = TAB_TO_GROUP[id];
    if (!buckets.has(groupId)) buckets.set(groupId, []);
    buckets.get(groupId).push(id);
  }
  const sortedGroups = Object.values(TAB_GROUPS).sort((a, b) => a.order - b.order);
  return sortedGroups
    .filter(g => buckets.has(g.id))
    .map(g => ({ group: g, tabs: buckets.get(g.id) }));
}

/**
 * Resolve the group id for a tab. Returns null for unknown tabs.
 */
export function getGroupForTab(tabId) {
  return TAB_TO_GROUP[tabId] || null;
}

/**
 * Resolve the group label (display string) for a tab. Returns null
 * for unknown tabs. Use this for section headers in the More-menu.
 */
export function getGroupLabelForTab(tabId) {
  const groupId = getGroupForTab(tabId);
  if (!groupId) return null;
  const group = Object.values(TAB_GROUPS).find((g) => g.id === groupId);
  return group?.label || null;
}

/**
 * Split a list of tabs into ordered groups. Returns
 *   [{ group: { id, label, order }, tabs: [...] }, ...]
 * sorted by group order. Tabs without a known group land in a
 * trailing "Other" pseudo-group (id='other') so they're still
 * reachable.
 *
 * The input tabs preserve their original order WITHIN each group —
 * callers control the within-group sequence by passing the array
 * in the order they want.
 *
 * @param {Array<{id: string}>} tabs
 * @returns {Array<{group, tabs}>}
 */
export function partitionTabsByGroup(tabs) {
  if (!Array.isArray(tabs) || tabs.length === 0) return [];
  const buckets = new Map();
  for (const tab of tabs) {
    const groupId = getGroupForTab(tab.id) || 'other';
    if (!buckets.has(groupId)) buckets.set(groupId, []);
    buckets.get(groupId).push(tab);
  }
  const knownGroups = Object.values(TAB_GROUPS).sort((a, b) => a.order - b.order);
  const result = [];
  for (const group of knownGroups) {
    if (buckets.has(group.id)) {
      result.push({ group, tabs: buckets.get(group.id) });
    }
  }
  if (buckets.has('other')) {
    result.push({
      group: { id: 'other', label: 'Other', order: 99 },
      tabs: buckets.get('other'),
    });
  }
  return result;
}
